import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ActionBar, Dialog, IssueIcon, Label, Modal, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { GenericTable } from '../../../components/GenericTable'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import type { User } from '../../users/types'
import { useCycleStore } from '../store'
import type { CycleAdmin, CycleAdminSearchInput } from './types'

type UserSearchInput = {
	search?: string
	page?: number
	perPage?: number
	sortBy?: 'email' | 'firstName' | 'lastName' | 'externalId'
	order?: 'asc' | 'desc'
}

export function CycleAdminsTab() {
	const { organizationId } = useParams<{ organizationId: string }>()
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [selectedUsers, setSelectedUsers] = useState<User[]>([])
	const [selectedAdminsToDelete, setSelectedAdminsToDelete] = useState<CycleAdmin[]>([])
	const queryClient = useQueryClient()
	const { showToast } = useToast()

	const cycle = useCycleStore((state) => state.cycle)

	const addCycleAdminsMutation = useMutation({
		...trpc.admin.cycles.admins.add.mutationOptions(),
		onSuccess: () => {
			showToast('User(s) set as cycle admin(s)')
			setIsModalOpen(false)
			setSelectedUsers([])
		},
		onError: () => {
			showToast('Something went wrong, the user(s) was(were) not set as a cycle admin(s)', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.admins.search.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const deleteCycleAdminsMutation = useMutation({
		...trpc.admin.cycles.admins.delete.mutationOptions(),
		onSuccess: () => {
			showToast('Cycle admin(s) removed')
			setSelectedAdminsToDelete([])
			setIsDeleteDialogOpen(false)
		},
		onError: () => {
			showToast('Something went wrong, the cycle admin(s) was(were) not removed', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.admins.search.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const handleOpenModal = () => {
		setIsModalOpen(true)
	}

	useEffect(() => {
		if (isModalOpen) {
			queryClient.invalidateQueries({
				queryKey: [['cyclesAdmins', 'searchPossibleAdmins']],
				exact: false,
				refetchType: 'all',
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isModalOpen, queryClient.invalidateQueries])

	const handleCloseModal = () => {
		setIsModalOpen(false)
		setSelectedUsers([])
	}

	const handleAddSelected = () => {
		if (!cycle || !organizationId) return
		addCycleAdminsMutation.mutate({
			cycleId: cycle.id,
			organizationId,
			userIds: selectedUsers.map((user) => user.id),
		})
	}

	const handleDeleteSelected = () => {
		if (!cycle || !organizationId) return
		deleteCycleAdminsMutation.mutate({
			cycleId: cycle.id,
			organizationId,
			userIds: selectedAdminsToDelete.map((admin) => admin.userId),
		})
	}

	const handleOpenDeleteDialog = () => {
		setIsDeleteDialogOpen(true)
	}

	const handleCloseDeleteDialog = () => {
		setIsDeleteDialogOpen(false)
	}

	return (
		<div className='mt-6'>
			<div className='border border-neutral-100 bg-neutral-0 rounded-lg shadow-xl'>
				<GenericTable<CycleAdmin, CycleAdminSearchInput>
					queryOptionsGenerator={(options) => ({
						...trpc.admin.cycles.admins.search.queryOptions({
							...options,
							organizationId: organizationId as string,
							cycleId: cycle?.id || '',
						}),
						enabled: !!cycle?.id,
					})}
					selectable
					onSelectionChange={setSelectedAdminsToDelete}
					columns={[
						{
							field: 'firstName',
							label: 'Name',
							colSpan: 2,
							render: (admin) => (
								<Text variant='xsmall' weight='regular'>
									{`${admin.firstName} ${admin.lastName}`}
								</Text>
							),
						},
						{
							field: 'externalId',
							label: 'ID',
							colSpan: 1,
						},
						{
							field: 'email',
							label: 'Email',
							colSpan: 2,
							render: (admin) => (
								<Text variant='xsmall' weight='regular'>
									{admin.email}
								</Text>
							),
						},
						{
							field: 'active',
							label: 'Status',
							colSpan: 1,
							render: (admin) => (
								<div className='flex'>
									<Label className='self-start' color={admin.active ? 'green' : 'default'}>
										{admin.active ? 'Active' : 'Inactive'}
									</Label>
								</div>
							),
						},
					]}
					showPagination
					actionButtons={[
						{
							children: 'Add user admin',
							onClick: handleOpenModal,
						},
					]}
					emptyState={{
						title: 'No cycle admins added yet',
						description: 'Get started by adding cycle admins',
						actionLabel: 'Add cycle admin',
						icon: IssueIcon,
						onAction: handleOpenModal,
					}}
				/>
			</div>

			<Modal isOpen={isModalOpen} fullContent onClose={handleCloseModal} fullScreen title='Add Cycle Admins'>
				<div className='flex flex-col h-[calc(100vh-150px)]'>
					<GenericTable<User, UserSearchInput>
						queryOptionsGenerator={(options) => ({
							...trpc.admin.cycles.admins.searchPossible.queryOptions({
								...options,
								organizationId: organizationId as string,
								cycleId: cycle?.id as string,
							}),
							enabled: !!cycle?.id && isModalOpen,
						})}
						isInternal
						selectable
						onSelectionChange={setSelectedUsers}
						columns={[
							{
								field: 'firstName',
								label: 'Name',
								colSpan: 2,
								render: (user) => (
									<Text variant='xsmall' weight='regular'>
										{`${user.firstName} ${user.lastName}`}
									</Text>
								),
							},
							{
								field: 'externalId',
								label: 'ID',
								colSpan: 1,
							},
							{
								field: 'email',
								label: 'Email',
								colSpan: 2,
								render: (user) => (
									<Text variant='xsmall' weight='regular'>
										{user.email}
									</Text>
								),
							},
							{
								field: 'active',
								label: 'Status',
								colSpan: 1,
								render: (user) => (
									<div className='flex'>
										<Label className='self-start' color={user.active ? 'green' : 'default'}>
											{user.active ? 'Active' : 'Inactive'}
										</Label>
									</div>
								),
							},
						]}
					/>
				</div>
				<ActionBar
					variant='modal'
					className='left-0 flex justify-end'
					rightActions={[
						{
							label: 'Cancel',
							variant: 'secondary',
							onClick: handleCloseModal,
						},
						{
							label: `Add user admins${selectedUsers.length > 0 ? ` (${selectedUsers.length})` : ''}`,
							variant: 'primary',
							onClick: handleAddSelected,
							disabled: selectedUsers.length === 0,
						},
					]}
				/>
			</Modal>

			{selectedAdminsToDelete.length > 0 && (
				<ActionBar
					rightActions={[
						{
							label: 'Cancel',
							variant: 'secondary',
							onClick: () => setSelectedAdminsToDelete([]),
						},
						{
							label: `Delete${selectedAdminsToDelete.length > 0 ? ` (${selectedAdminsToDelete.length})` : ''}`,
							variant: 'destructive',
							onClick: handleOpenDeleteDialog,
						},
					]}
				/>
			)}

			<Dialog
				isOpen={isDeleteDialogOpen}
				onClose={handleCloseDeleteDialog}
				onConfirm={handleDeleteSelected}
				title='Delete cycle admins'
				message={`Are you sure you want to delete ${selectedAdminsToDelete.length} cycle admin${selectedAdminsToDelete.length === 1 ? '' : 's'}?`}
				type='delete'
				confirmLabel='Delete'
			/>
		</div>
	)
}
